/**
 * Aba Simulador — preço livre digitado. Comissão pela regra do degrau de R$ 12,50 (24/08/2026),
 * frete pré-preenchido com o REAL da API no preço atual (editável — o ML cobra em degraus),
 * CMV do cadastro (ou digitado), imposto pela base do botão do topo. É a única estimativa da tela.
 */
import { useEffect, useState } from 'react'
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { classeMc, simular } from './logic'
import { usePromo } from './ctx'
import { P, Panel, brl, num, pct, useCorMc } from './ui'

export function AbaSimulador({ mlbSel, setMlbSel }: { mlbSel: string | null; setMlbSel: (m: string | null) => void }) {
  const ctx = usePromo()
  const { conta, base, cmvManual, alvo } = ctx
  const cor = useCorMc()
  const a = (mlbSel ? conta.ads.find((x) => x.mlb === mlbSel) : null) ?? conta.ads[0] ?? null
  const [preco, setPreco] = useState('')
  const [frete, setFrete] = useState('')

  useEffect(() => {
    if (!a) return
    setPreco(String(a.preco_atual))
    setFrete(a.frete_atual_api != null ? String(a.frete_atual_api) : '')
  }, [a?.mlb]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!a) return <Panel><P>Nenhum anúncio na base.</P></Panel>
  const p = parseFloat(preco.replace(',', '.')) || 0
  const f = parseFloat(frete.replace(',', '.')) || 0
  const s = simular(a, p, f, base, cmvManual)
  const classe = classeMc({ rs: s.mc, pct: s.pct, faltas: [] }, alvo)

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 2.5 }}>
      <Panel>
        <Stack spacing={1.5}>
          <Autocomplete options={conta.ads.map((x) => x.mlb)} value={a.mlb} onChange={(_, v) => setMlbSel(v)}
            renderInput={(params) => <TextField {...params} size="small" label="Anúncio" />}
            filterOptions={(_, state) => { const q = state.inputValue.trim().toLowerCase(); return (q ? conta.ads.filter((x) => `${x.titulo} ${x.mlb} ${x.sku ?? ''}`.toLowerCase().includes(q)) : conta.ads).slice(0, 300).map((x) => x.mlb) }}
            getOptionLabel={(mlb) => { const x = conta.ads.find((y) => y.mlb === mlb); return x ? `${x.titulo} — ${x.mlb}` : mlb }} />
          <TextField size="small" label="Preço promocional (R$)" value={preco} onChange={(e) => setPreco(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          <TextField size="small" label="Frete (R$, real da API no preço atual)" value={frete} onChange={(e) => setFrete(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
          <P>Ajuste o preço e a conta refaz — comissão usa a regra do degrau de R$ 12,50 (fixa 50% até lá), CMV vem do cadastro (ou do que você digitou na aba Por anúncio), imposto usa a alíquota da base escolhida no topo. Frete vem pré-preenchido com o valor real da API (shipping_options/free) no preço atual do anúncio — o frete do ML muda em degraus por preço, então se o preço simulado for bem diferente do atual, edite o campo com o valor real daquele preço (não invente).</P>
        </Stack>
      </Panel>
      <Panel title="Resultado simulado">
        <Stack spacing={0}>
          {s.linhas.map((l, i) => (
            <Box key={l.rotulo} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderTop: i ? 1 : 0, borderColor: 'divider', fontSize: 13.5 }}>
              <Box><span>{l.rotulo}</span>{l.nota ? <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{l.nota}</Typography> : null}</Box>
              <Box component="b" sx={{ ...num, whiteSpace: 'nowrap', color: l.valor == null ? 'warning.main' : undefined, fontWeight: 600 }}>{l.valor == null ? (i === 3 ? 'ausente — fora do cálculo' : 'fora da conta') : `${i ? '− ' : ''}${brl(l.valor)}`}</Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, mt: 0.5, borderTop: 1, borderColor: 'text.disabled', fontSize: 16 }}>
            <span>MC simulada</span>
            <Box component="b" sx={{ ...num, color: cor(classe) }}>{s.mc == null ? '—' : brl(s.mc)}{s.pct != null ? ` (${pct(s.pct)})` : ''}</Box>
          </Box>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>Estimativa, rotulada como tal: comissão e frete não são consultados na API a cada valor digitado. Para a margem real de uma promoção, use a aba Promoções ou Por anúncio.</Typography>
        </Stack>
      </Panel>
    </Box>
  )
}
